'use client';

import { useState, useEffect } from 'react';
import { createConfig, http, type Config } from 'wagmi';
import { mainnet } from 'wagmi/chains';
import type { RainbowKitWalletConnectParameters } from '@rainbow-me/rainbowkit';
import { Env } from '@/libs/env';

// 🔥 Polyfill for crypto.randomUUID() for older browsers
// Covers edge cases in Indian market: Chrome < 92, Safari < 15.4
// Issue: Crypto.prototype.randomUUID was missing due to aggressive browserslist targeting iOS 9/Android 4.4
if (
  typeof window !== 'undefined' &&
  typeof Crypto !== 'undefined' &&
  Crypto.prototype &&
  typeof Crypto.prototype.randomUUID !== 'function'
) {
  Crypto.prototype.randomUUID = function randomUUID(): `${string}-${string}-${string}-${string}-${string}` {
    // Generate RFC 4122 version 4 UUID using getRandomValues
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const array = new Uint8Array(1);
      this.getRandomValues(array);
      const r = array[0] % 16;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    }) as `${string}-${string}-${string}-${string}-${string}`;
  };
}

const projectId = '98beed205b114775d73762c6595f177e';
const appName = Env.NEXT_PUBLIC_APP_NAME;
const appDescription = Env.NEXT_PUBLIC_APP_SLOGAN;

interface ComputeMetaDataParameters {
  appName: string;
  appDescription?: string;
  appUrl?: string;
  appIcon?: string;
}

export const computeWalletConnectMetaData = ({
  appName,
  appDescription,
  appUrl,
  appIcon,
}: ComputeMetaDataParameters): RainbowKitWalletConnectParameters['metadata'] => {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const defaultIcon = origin ? `${origin}/favicon-48x48.png` : '';

  return {
    name: appName,
    description: appDescription ?? appName,
    url: appUrl ?? origin,
    icons: [...(appIcon ? [appIcon] : defaultIcon ? [defaultIcon] : [])],
  };
};

// 创建一个可以在服务器端和客户端都使用的基础配置
const baseConfig = createConfig({
  chains: [mainnet],
  transports: {
    [mainnet.id]: http(),
  },
  multiInjectedProviderDiscovery: false,
  ssr: true,
});

/**
 * 这是一个自定义 hook，用于在客户端动态加载 Wagmi 连接器。
 * 在 SSR 阶段，它会立即返回一个不包含连接器的基础配置。
 */
export function useWagmiConfig() {
  const [wagmiConfig, setWagmiConfig] = useState<Config>(baseConfig);

  useEffect(() => {
    async function loadConnectors() {
      // 动态导入连接器
      const { connectorsForWallets } = await import('@rainbow-me/rainbowkit');
      const {
        metaMaskWallet,
        okxWallet,
        rabbyWallet,
        trustWallet,
        phantomWallet,
      } = await import('@rainbow-me/rainbowkit/wallets');

      // baseAccount.subAccounts = {
      //   creation: 'on-connect',
      //   defaultAccount: 'sub',
      //   funding: 'spend-permissions',
      // };

      // baseAccount.preference = {
      //   attribution: { auto: true },
      // };

      const connectors = connectorsForWallets(
        [
          {
            groupName: 'Popular',
            wallets: [
              metaMaskWallet,
              okxWallet,
              trustWallet,
              phantomWallet,
              rabbyWallet,
            ],
          },
        ],
        {
          projectId,
          appName,
          appDescription,
          walletConnectParameters: {
            metadata: computeWalletConnectMetaData({
              appName,
              appDescription,
            }),
          },
        },
      );

      // 创建一个包含连接器的完整配置
      const fullConfig = createConfig({
        chains: [mainnet],
        transports: {
          [mainnet.id]: http(),
        },
        multiInjectedProviderDiscovery: false,
        ssr: true,
        connectors: [...connectors],
      });

      setWagmiConfig(fullConfig);
    }

    loadConnectors();
  }, []);

  return wagmiConfig;
}